import React from 'react';
import { IHasTestData } from '../../test-utilities/IHasTestData';
import {
  INavigationButtonProps,
  StyledNavigationItemButton,
  StyledNavigationItemButtonAsButton,
  StyledNavigationItemLink,
  StyledNavigationItemLinkAsButton,
  StyledRouterLink,
  StyledRouterLinkAsButton,
} from './NavigationItem.Styled';
import { mergeTests } from '../../test-utilities/mergeTests';
import { useHasFeatureFlag } from '../../../selectors/useHasFeatureFlag';

export interface INavigationItemProps extends INavigationButtonProps, IHasTestData {
  /* onClick means you want a button, and takes priority over to */
  onClick?: (href?: string) => void;
  /* href means it will be a simple link, and is less important than onClick. External Links */
  href?: string;
  /* to means that it's an internal link, and is less important than onclick/href. Internal Links */
  to?: string;
  /* The identifier for this navigation item. Will display inside the button/anchor. Will be replaced by children if present. Also used as the title tag. */
  text: string;
  /** Target for the Anchor tag. If you do not use HREF or TO, and just use onclick, this will not work. */
  target?: string;
  rel?: 'noreferrer';
  children?: React.ReactNode;
}

export interface IGroupedNavigationOptions {
  group?: string; // name of the group
  navigationOptions: INavigationItemProps[];
}

/* A navigation item will dynamically be a button or a link depending on what properties are set. However, they will look and behave basically the same */
export const NavigationItem: React.FunctionComponent<INavigationItemProps> = ({
  text,
  isActive,
  onClick,
  href,
  children,
  to,
  intent,
  appearance,
  ...rest
}) => {
  const isZillowClosingEnabled = useHasFeatureFlag('pinecone.zillow-closing');
  const useSpruceColors = !isZillowClosingEnabled;

  const onLinkClick = (e: React.MouseEvent) => {
    if (onClick) {
      e.preventDefault();
      onClick(href || to);
    }
  };
  const dataTestClass = rest['data-test-class'] || 'link';

  if (href) {
    const linkProps = {
      href,
      onClick: onLinkClick,
      isActive,
      title: text,
      $useSpruceColors: useSpruceColors,
      ...rest,
      'data-test-class': mergeTests(dataTestClass, 'link'),
    };
    if (intent && appearance) {
      return (
        <StyledNavigationItemLinkAsButton
          {...linkProps}
          intent={intent}
          appearance={appearance}
          $useSpruceColors={useSpruceColors}
        >
          {children || text}
        </StyledNavigationItemLinkAsButton>
      );
    }
    return <StyledNavigationItemLink {...linkProps}>{children || text}</StyledNavigationItemLink>;
  }

  if (to) {
    const routerLinkProps = {
      to,
      onClick: onLinkClick,
      isActive,
      title: text,
      $useSpruceColors: useSpruceColors,
      ...rest,
      'data-test-class': mergeTests(dataTestClass, 'link'),
    };
    if (intent && appearance) {
      return (
        <StyledRouterLinkAsButton
          {...routerLinkProps}
          intent={intent}
          appearance={appearance}
          $useSpruceColors={useSpruceColors}
        >
          {children || text}
        </StyledRouterLinkAsButton>
      );
    }
    return <StyledRouterLink {...routerLinkProps}>{children || text}</StyledRouterLink>;
  }

  const baseProps = {
    isActive,
    onClick: onLinkClick,
    title: text,
    $useSpruceColors: useSpruceColors,
    ...rest,
    'data-test-class': mergeTests(dataTestClass, 'button'),
  };

  if (intent && appearance) {
    return (
      <StyledNavigationItemButtonAsButton
        {...baseProps}
        intent={intent}
        appearance={appearance}
        $useSpruceColors={useSpruceColors}
      >
        {children || text}
      </StyledNavigationItemButtonAsButton>
    );
  }

  return (
    <StyledNavigationItemButton
      isActive={isActive}
      onClick={onLinkClick}
      title={text}
      $useSpruceColors={useSpruceColors}
      {...rest}
      data-test-class={mergeTests(dataTestClass, 'button')}
    >
      {children || text}
    </StyledNavigationItemButton>
  );
};

export const intoNavigationItems = (x: INavigationItemProps) => <NavigationItem {...x} />;
